import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Loader2, Search, Radar, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../utils/axios';
import { ReglasForm } from './ReglasForm';
import { fmtMoney, REGLAS_DEFAULT, reglasToApi, toNum } from './vigilanciaUtils';

const Candidato = ({ c, selected, onSelect }) => (
  <button
    type="button" disabled={c.ya_vigilada} onClick={() => onSelect(c)}
    className={`w-full text-left rounded-xl border p-3 transition-colors duration-200 ${selected ? 'border-teal-500 bg-teal-50/60 ring-1 ring-teal-500' : 'border-slate-200 hover:border-slate-300 bg-white'} ${c.ya_vigilada ? 'opacity-60 cursor-not-allowed' : ''}`}
    data-testid={`vig-candidato-${c.cvu || c.cuit}`}
  >
    <div className="flex items-start justify-between gap-2">
      <div className="min-w-0">
        <p className="font-semibold text-slate-900 truncate">{c.nombre}</p>
        <p className="text-xs text-slate-500 truncate">{c.email} · CUIT {c.cuit}</p>
        <p className="font-mono text-[11px] text-slate-500">CVU {c.cvu || '—'}</p>
      </div>
      <div className="flex flex-col items-end gap-1 shrink-0">
        <Badge variant="outline" className="text-[10px]">{c.estado || '—'}</Badge>
        {c.ya_vigilada && <Badge className="bg-slate-200 text-slate-700 text-[10px]">Ya vigilada</Badge>}
        {selected && <CheckCircle2 size={16} className="text-teal-600" />}
      </div>
    </div>
    <div className="mt-2 grid grid-cols-3 gap-2 text-[11px]">
      <div><p className="text-slate-400">Saldo</p><p className="font-semibold tabular-nums text-slate-800">{fmtMoney(c.disponible)}</p></div>
      <div><p className="text-slate-400">Diario usado</p><p className="font-semibold tabular-nums text-slate-800">{fmtMoney(c.consumido_diario)} / {fmtMoney(c.limite_diario)}</p></div>
      <div><p className="text-slate-400">Mensual usado</p><p className="font-semibold tabular-nums text-slate-800">{fmtMoney(c.consumido_mensual)} / {fmtMoney(c.limite_mensual)}</p></div>
    </div>
  </button>
);

export const AgregarVigilanciaDialog = ({ open, onOpenChange, initialTermino = '', onCreated }) => {
  const [termino, setTermino] = useState(initialTermino);
  const [buscando, setBuscando] = useState(false);
  const [items, setItems] = useState(null);
  const [sel, setSel] = useState(null);
  const [form, setForm] = useState({ intervalo_min: 60, saldo_referencia: '', reglas: REGLAS_DEFAULT });
  const [guardando, setGuardando] = useState(false);

  const buscar = async (t = termino) => {
    if (!t.trim()) return;
    setBuscando(true); setItems(null); setSel(null);
    try {
      const { data } = await api.post('/vigilancia/buscar', { termino: t }, { timeout: 120000 });
      if (!data.ok) { toast.error(data.error); setItems([]); return; }
      setItems(data.items);
      const libres = data.items.filter((i) => !i.ya_vigilada);
      if (libres.length === 1) setSel(libres[0]);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo consultar Waya');
      setItems([]);
    } finally { setBuscando(false); }
  };

  useEffect(() => {
    if (!open) return;
    setTermino(initialTermino); setItems(null); setSel(null);
    setForm({ intervalo_min: 60, saldo_referencia: '', reglas: REGLAS_DEFAULT });
    if (initialTermino) buscar(initialTermino);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialTermino]);

  const guardar = async () => {
    setGuardando(true);
    try {
      const { data } = await api.post('/vigilancia/cuentas', {
        cvu: sel.cvu, cuit: sel.cuit, email: sel.email, intervalo_min: form.intervalo_min,
        saldo_referencia: toNum(form.saldo_referencia), reglas: reglasToApi(form.reglas),
      });
      toast.success(`${data.nombre || 'Cuenta'} quedó en vigilancia`);
      onCreated?.(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo crear la vigilancia');
    } finally { setGuardando(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="vig-scope max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="vig-agregar-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Radar size={18} className="text-teal-700" /> Vigilar una cuenta</DialogTitle>
          <DialogDescription>Ingresá el CVU o el CUIT. Buscamos la cuenta en Waya (Reporte de Billeteras y Gestión de Cuentas).</DialogDescription>
        </DialogHeader>
        <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); buscar(); }}>
          <Input
            value={termino} onChange={(e) => setTermino(e.target.value)} placeholder="CVU (22 dígitos) o CUIT (11 dígitos)"
            className="font-mono" data-testid="vig-buscar-input" autoFocus
          />
          <Button type="submit" disabled={buscando || !termino.trim()} className="bg-teal-700 hover:bg-teal-800" data-testid="vig-buscar-btn">
            {buscando ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />}
            <span className="ml-1.5">Buscar</span>
          </Button>
        </form>
        {buscando && <p className="text-sm text-slate-500 flex items-center gap-2" data-testid="vig-buscando"><Loader2 size={14} className="animate-spin" /> Consultando Waya… puede tardar unos segundos</p>}
        {items && items.length === 0 && <p className="text-sm text-slate-500" data-testid="vig-sin-resultados">No se encontró ninguna cuenta en Waya.</p>}
        {items && items.length > 0 && (
          <div className="space-y-2" data-testid="vig-candidatos">
            {items.map((c) => <Candidato key={c.cvu || c.email} c={c} selected={sel === c} onSelect={setSel} />)}
          </div>
        )}
        {sel && <ReglasForm value={form} onChange={setForm} />}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={guardar} disabled={!sel || guardando} className="bg-teal-700 hover:bg-teal-800" data-testid="vig-guardar-btn">
            {guardando && <Loader2 size={16} className="mr-1.5 animate-spin" />} Vigilar cuenta
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AgregarVigilanciaDialog;
